import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { requireAuth } from "~/lib/auth-guard";
import { useJobStore } from "~/stores/job-store";
import { useResumeStore } from "~/stores/resume-store";
import { calculateAtsScore, type AtsScoreResult } from "~/lib/ats-score";
import { AiProcessingNotice } from "~/components/AiProcessingNotice";
import { ResumeDiff } from "~/components/ResumeDiff";
import { AtsScoreCard } from "~/components/AtsScoreCard";
import { AiActionButton } from "~/components/AiActionButton";
import { CompanyPanel } from "~/components/CompanyPanel";
import { SalaryPercentileBar } from "~/components/SalaryPercentileBar";
import { AtsBadge } from "~/components/AtsBadge";
import { ExportButton } from "~/components/ExportButton";
import { detectAtsFromUrl, getAtsFormatRules } from "~/lib/ats-detection";
import { useToast } from "@alchema/ui";
import { orpc, client } from "~/lib/orpc-client";
import type { ParsedRequirements, ResumeData, OutputLanguage } from "@alchema/shared";
import type { Tier } from "~/lib/tier";
import * as m from "~/paraglide/messages";
import { trackEvent } from "~/lib/posthog-client";
import { SwarmProgressPanel } from "~/components/swarm/SwarmProgressPanel";
import { startSwarmProgress } from "~/lib/swarm/progress-bridge";
import { useSwarmProgressStore } from "~/lib/stores/swarm-progress";
import { AiGeneratedBadge } from "~/components/AiGeneratedBadge";
import { OutputLanguageSelect } from "~/components/OutputLanguageSelect";

export const Route = createFileRoute("/jobs/$jobId")({
  beforeLoad: async () => { await requireAuth(); },
  component: JobDetailPage,
});

function JobDetailPage() {
  const { jobId } = Route.useParams();
  const navigate = useNavigate();
  const jobStore = useJobStore();
  const resumeStore = useResumeStore();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  // Fetch from server first, fall back to Zustand cache
  const jobQuery = useQuery({
    ...orpc.job.get.queryOptions({ input: { id: jobId } }),
    staleTime: 60_000,
    retry: 0,
  });
  const job = jobQuery.data ?? jobStore.getJob(jobId) ?? null;

  const [analyzing, setAnalyzing] = React.useState(false);
  const [tailoring, setTailoring] = React.useState(false);
  const [error, setError] = React.useState("");
  const [tailoredData, setTailoredData] = React.useState<ResumeData | null>(null);
  const [atsScore, setAtsScore] = React.useState<AtsScoreResult | null>(null);
  const [panelOpen, setPanelOpen] = React.useState(false);
  const [outputLanguage, setOutputLanguage] = React.useState("en");
  const [sectionDecisions, setSectionDecisions] = React.useState<Record<string, "accepted" | "rejected">>({});

  // Client-side ATS detection from job URL (synchronous, pure regex)
  const atsResult = React.useMemo(
    () => job?.url ? detectAtsFromUrl(job.url) : null,
    [job?.url],
  );
  const atsRules = React.useMemo(
    () => atsResult?.provider ? getAtsFormatRules(atsResult.provider) : null,
    [atsResult?.provider],
  );

  // Free salary benchmark — no tier check, no credits
  const salaryQuery = useQuery({
    ...orpc.salary.benchmarkFree.queryOptions({
      input: { title: job?.title ?? "", location: "DE" },
    }),
    enabled: !!job?.title,
    retry: 0,
  });
  const salaryData = salaryQuery.data;

  React.useEffect(() => {
    if (job) {
      trackEvent("job_viewed", {});
    }
  }, [jobId, job?.title]);

  React.useEffect(() => {
    const detected = (job?.parsedRequirements as Record<string, unknown> | null)?.detectedLanguage;
    if (typeof detected === "string" && detected) {
      setOutputLanguage(detected);
    }
  }, [job?.parsedRequirements]);

  React.useEffect(() => {
    return () => { useSwarmProgressStore.getState().reset(); };
  }, []);

  const applyMutation = useMutation(orpc.application.create.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orpc.application.list.key({}) });
      toast(m.jobs_marked_applied(), "success");
      // Fire enrichment in background on apply (best-effort)
      if (job?.url) {
        client.enrichment.trigger({ url: job.url, jobId }).catch(() => {});
        // Check after 5s if enrichment completed, show toast
        setTimeout(async () => {
          try {
            const domain = new URL(job.url!).hostname.replace(/^www\./, "");
            const company = await client.enrichment.company({ domain });
            if (company) {
              toast("Job enriched — company info, salary data, and ATS format ready", "success");
            }
          } catch { /* ignore */ }
        }, 5000);
      }
    },
    onError: () => {
      toast(m.jobs_marked_applied_error(), "error");
    },
  }));

  const creditQuery = useQuery(orpc.credits.status.queryOptions({ input: undefined }));
  const credits = creditQuery.data;

  // Fetch resumes from server — Zustand store may be empty on direct page load
  const resumeListQuery = useQuery({
    ...orpc.resume.list.queryOptions({ input: undefined }),
    staleTime: 60_000,
  });
  // Server-authoritative: use server data only, Zustand store has stale IDs
  const serverResumes = resumeListQuery.data as Array<{ id: string; isMaster?: boolean; title: string; data: ResumeData }> | undefined;
  const masterResume = serverResumes?.find((r) => r.isMaster) ?? serverResumes?.[0];
  const variants = jobStore.getVariantsForJob(jobId);

  const analyzeMutation = useMutation(orpc.job.analyze.mutationOptions());
  const tailorMutation = useMutation(orpc.job.tailor.mutationOptions());

  if (jobQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-20" role="status" aria-label={m.common_loading()}>
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[var(--border)] border-t-[var(--text)]" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="flex flex-col items-center justify-center py-20 animate-fade-up">
        <div className="card card-rose flex h-16 w-16 items-center justify-center">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
        </div>
        <p className="mt-4 text-[var(--text-muted)]">{m.jobs_not_found()}</p>
        <button onClick={() => navigate({ to: "/jobs" })} className="btn btn-ghost btn-sm mt-3">
          {m.jobs_back_to_jobs()}
        </button>
      </div>
    );
  }

  async function analyzeJob() {
    setAnalyzing(true);
    setError("");
    try {
      const result = await analyzeMutation.mutateAsync({
        id: jobId,
        description: job!.description ?? "",
      });
      jobStore.updateJob(jobId, { parsedRequirements: result.requirements });
    } catch (err: unknown) {
      setError(`Analysis failed: ${err instanceof Error ? err.message : "Unknown error"}`);
    } finally {
      setAnalyzing(false);
    }
  }

  async function tailorResume() {
    if (!masterResume || !job?.parsedRequirements) return;
    setTailoring(true);
    setError("");
    const progress = startSwarmProgress('resume-tailoring');
    try {
      const result = await tailorMutation.mutateAsync({
        jobId,
        resumeId: masterResume.id,
        outputLanguage: outputLanguage as OutputLanguage,
      });
      setTailoredData(result.tailoredData);
      if (job.parsedRequirements) {
        const score = calculateAtsScore(result.tailoredData, job.parsedRequirements);
        setAtsScore(score);
        progress.complete(score.overall);
      } else {
        progress.complete();
      }
    } catch (err: unknown) {
      progress.fail();
      setError(`Tailoring failed: ${err instanceof Error ? err.message : "Unknown error"}`);
    } finally {
      setTailoring(false);
    }
  }

  function saveVariant() {
    if (!tailoredData || !masterResume) return;
    jobStore.addVariant({
      jobId,
      sourceResumeId: masterResume.id,
      data: tailoredData,
      atsScore: atsScore?.overall || null,
    });
    setTailoredData(null);
    setAtsScore(null);
    setSectionDecisions({});
  }

  function handleAcceptSection(section: string) {
    setSectionDecisions((prev) => ({ ...prev, [section]: "accepted" }));
    toast(m.jobs_section_accepted(), "success");
  }

  function handleRejectSection(section: string) {
    if (!tailoredData || !masterResume) return;
    const updated = { ...tailoredData };
    switch (section) {
      case "summary": updated.summary = masterResume.data.summary; break;
      case "work": updated.work = masterResume.data.work; break;
      case "education": updated.education = masterResume.data.education; break;
      case "skills": updated.skills = masterResume.data.skills; break;
      case "languages": updated.languages = masterResume.data.languages; break;
      case "certificates": updated.certificates = masterResume.data.certificates; break;
    }
    setTailoredData(updated);
    setSectionDecisions((prev) => ({ ...prev, [section]: "rejected" }));
    if (job?.parsedRequirements) {
      setAtsScore(calculateAtsScore(updated, job.parsedRequirements));
    }
    toast(m.jobs_section_rejected(), "default");
  }

  const statusOptions: Array<{ value: string; label: string }> = [
    { value: "saved", label: m.jobs_status_saved() },
    { value: "applied", label: m.jobs_status_applied() },
    { value: "interview", label: m.jobs_status_interview() },
    { value: "rejected", label: m.jobs_status_rejected() },
    { value: "offer", label: m.jobs_status_offer() },
  ];

  const statusBadgeClass: Record<string, string> = {
    saved: "badge badge-default",
    applied: "badge badge-accent",
    interview: "badge badge-warning",
    rejected: "badge badge-error",
    offer: "badge badge-success",
  };

  return (
    <div className="mx-auto max-w-4xl animate-fade-up">
      {/* Breadcrumb */}
      <div className="mb-6 flex items-center gap-2 text-sm text-[var(--text-muted)] animate-fade-up stagger-1">
        <Link to="/jobs" className="hover:text-[var(--text-secondary)] transition-colors">
          {m.jobs_title()}
        </Link>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 18 15 12 9 6" />
        </svg>
        <span className="text-[var(--text)] truncate max-w-[200px]">{job.title}</span>
      </div>

      {/* Header Card */}
      <div className="card p-6 animate-fade-up stagger-2">
        <AiProcessingNotice kind="application" className="mb-4" />
        <div className="flex items-start justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3">
              <h1 className="page-title truncate">{job.title}</h1>
              <span className={statusBadgeClass[job.status] || "badge badge-default"}>
                {statusOptions.find((o) => o.value === job.status)?.label || job.status}
              </span>
              {atsResult?.provider && atsRules && (
                <AtsBadge
                  provider={atsResult.provider.charAt(0).toUpperCase() + atsResult.provider.slice(1)}
                  tip={atsRules.tip}
                />
              )}
            </div>
            {job.url ? (
              <button
                onClick={() => setPanelOpen(true)}
                className="mt-1 text-sm text-[var(--text-muted)] hover:text-[var(--accent)] hover:underline transition-colors cursor-pointer"
              >
                {job.company}
              </button>
            ) : (
              <p className="mt-1 text-sm text-[var(--text-muted)]">{job.company}</p>
            )}
            {job.url && (
              <a
                href={job.url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1 inline-flex items-center gap-1 text-xs text-[var(--accent)] hover:underline"
              >
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
                {m.jobs_view_original_posting()}
              </a>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <select
              value={job.status}
              onChange={(e) => jobStore.updateJob(jobId, { status: e.target.value as import("@alchema/shared").JobStatus })}
              className="input text-xs py-1.5 px-2"
            >
              {statusOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <button
              onClick={() => applyMutation.mutate({ jobId, method: "manual" })}
              disabled={applyMutation.isPending}
              className="btn btn-accent btn-sm"
            >
              {applyMutation.isPending ? "..." : m.jobs_mark_applied()}
            </button>
            <button
              onClick={() => navigate({ to: "/jobs" })}
              className="btn btn-ghost btn-sm"
            >
              {m.common_back()}
            </button>
          </div>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="card card-rose mt-4 flex items-start gap-3 p-4 animate-fade-up">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--error)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 shrink-0">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
          <p className="text-sm text-[var(--error)]">{error}</p>
        </div>
      )}

      {/* Job Description */}
      <div className="card mt-4 p-6 animate-fade-up stagger-3">
        <div className="flex items-center gap-2 mb-4">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[var(--bg-secondary)]">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
          </div>
          <h2 className="text-sm font-semibold text-[var(--text)]">{m.jobs_description()}</h2>
        </div>
        <div className="max-h-72 overflow-auto whitespace-pre-wrap rounded-lg bg-[var(--bg-secondary)] p-4 text-xs leading-relaxed text-[var(--text-secondary)]">
          {job.description || m.jobs_no_description()}
        </div>
      </div>

      {/* Salary Percentile Bar */}
      {salaryData && salaryData.q25 && salaryData.median && salaryData.q75 && (
        <div className="mt-4 animate-fade-up stagger-4">
          <SalaryPercentileBar
            q25={salaryData.q25}
            median={salaryData.median}
            q75={salaryData.q75}
            source={salaryData.source ?? "Salary data"}
          />
        </div>
      )}

      {/* AI Analysis */}
      <div className="card mt-4 p-6 animate-fade-up stagger-4">
        <div className="flex items-center gap-2 mb-4">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[var(--accent-subtle)]">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2a4 4 0 014 4v2a4 4 0 01-8 0V6a4 4 0 014-4z" />
              <path d="M6 10v1a6 6 0 0012 0v-1" />
              <line x1="12" y1="17" x2="12" y2="22" />
              <line x1="8" y1="22" x2="16" y2="22" />
            </svg>
          </div>
          <h2 className="text-sm font-semibold text-[var(--text)]">{m.jobs_ai_analysis()}</h2>
        </div>

        {!job.parsedRequirements ? (
          <div className="rounded-lg border border-dashed border-[var(--border)] p-6 text-center">
            <p className="text-xs text-[var(--text-muted)]">
              {m.jobs_analyze_extract_desc()}
            </p>
            <AiProcessingNotice id="job-analysis-ai-processing-notice" variant="compact" className="mt-3 text-left" />
            <button
              onClick={analyzeJob}
              disabled={analyzing}
              aria-describedby="job-analysis-ai-processing-notice"
              className="btn btn-accent btn-sm mt-3"
            >
              {analyzing ? (
                <span className="flex items-center gap-1.5">
                  <svg className="h-3.5 w-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="32" strokeLinecap="round" />
                  </svg>
                  {m.jobs_analyzing()}
                </span>
              ) : (
                m.jobs_analyze_with_ai()
              )}
            </button>
          </div>
        ) : (
          <RequirementsDisplay requirements={job.parsedRequirements} />
        )}
      </div>

      {/* Swarm Progress */}
      <div className="mt-4">
        <SwarmProgressPanel />
      </div>

      {/* Tailor Resume */}
      {job.parsedRequirements && (
        <div className="card card-lavender mt-4 p-6 animate-fade-up stagger-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[var(--accent-subtle)]">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </div>
            <h2 className="text-sm font-semibold text-[var(--text)]">{m.jobs_tailor_resume()}</h2>
          </div>

          {!masterResume ? (
            <div className="rounded-lg border border-dashed border-[var(--border)] p-6 text-center">
              <p className="text-xs text-[var(--text-muted)]">
                {m.jobs_no_master_resume()}
              </p>
              <Link to="/resumes" className="btn btn-secondary btn-sm mt-3">
                {m.jobs_go_to_resumes()}
              </Link>
            </div>
          ) : !tailoredData ? (
            <div className="rounded-lg border border-dashed border-[var(--border)] p-6 text-center">
              <p className="text-xs text-[var(--text-muted)]">
                {m.jobs_tailor_resume_desc({ title: masterResume.title })}
              </p>
              <AiProcessingNotice id="job-tailor-ai-processing-notice" variant="compact" className="mb-3 text-left" />
              <OutputLanguageSelect
                value={outputLanguage}
                onChange={setOutputLanguage}
                detectedLanguage={(job?.parsedRequirements as Record<string, unknown> | null)?.detectedLanguage as string | undefined}
                className="mb-3"
              />
              <AiActionButton
                creditCost={1}
                onClick={tailorResume}
                disabled={tailoring}
                ariaDescribedBy="job-tailor-ai-processing-notice"
                creditsRemaining={credits?.totalAvailable ?? credits?.balance ?? 0}
                creditLimit={credits?.limit ?? 0}
                creditResetAt={credits?.resetAt ? new Date(credits.resetAt) : new Date()}
                tier={(credits?.tier as Tier) ?? "free"}
                className="btn btn-accent btn-sm mt-3"
              >
                {tailoring ? (
                  <span className="flex items-center gap-1.5">
                    <svg className="h-3.5 w-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="32" strokeLinecap="round" />
                    </svg>
                    {m.jobs_tailoring()}
                  </span>
                ) : (
                  m.jobs_generate_tailored_resume()
                )}
              </AiActionButton>
            </div>
          ) : (
            <div className="space-y-6">
              {salaryData && salaryData.q25 && salaryData.median && salaryData.q75 && (
                <div className="mb-4">
                  <SalaryPercentileBar
                    q25={salaryData.q25}
                    median={salaryData.median}
                    q75={salaryData.q75}
                    source={salaryData.source ?? "Salary data"}
                    compact
                  />
                </div>
              )}
              {atsScore && <AtsScoreCard score={atsScore} />}
              <div className="mb-2 flex justify-end">
                <AiGeneratedBadge />
              </div>
              <ResumeDiff
                original={masterResume.data}
                tailored={tailoredData}
                onAcceptSection={handleAcceptSection}
                onRejectSection={handleRejectSection}
                sectionDecisions={sectionDecisions}
              />
              <div className="flex gap-2">
                <button onClick={saveVariant} className="btn btn-primary btn-sm">
                  {m.jobs_save_variant()}
                </button>
                <ExportButton data={tailoredData} atsProvider={atsResult?.provider ?? null} />
                <button
                  onClick={() => { setTailoredData(null); setAtsScore(null); setSectionDecisions({}); }}
                  className="btn btn-ghost btn-sm"
                >
                  {m.jobs_discard()}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Saved Variants */}
      {variants.length > 0 && (
        <div className="card mt-4 p-6 animate-fade-up stagger-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[var(--bg-secondary)]">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M13 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V9z" />
                <polyline points="13 2 13 9 20 9" />
              </svg>
            </div>
            <h2 className="text-sm font-semibold text-[var(--text)]">
              {m.jobs_saved_variants()}
              <span className="ml-2 badge badge-default">{variants.length}</span>
            </h2>
          </div>
          <div className="space-y-2">
            {variants.map((v) => (
              <div key={v.id} className="flex items-center justify-between rounded-xl bg-[var(--bg-secondary)] p-4 transition-colors hover:bg-[var(--surface-raised)]">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[var(--surface)]">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[var(--text)]">
                      {m.jobs_variant_from_date({ date: new Date(v.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) })}
                    </p>
                    <div className="mt-0.5 flex items-center gap-2">
                      {v.atsScore !== null ? (
                        <span className={`badge ${v.atsScore >= 80 ? "badge-success" : v.atsScore >= 60 ? "badge-warning" : "badge-error"}`}>
                          {m.jobs_variant_ats_score({ score: v.atsScore })}
                        </span>
                      ) : (
                        <span className="badge badge-default">{m.jobs_variant_ats_na()}</span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => jobStore.deleteVariant(v.id)}
                  className="btn btn-ghost btn-sm text-[var(--error)] hover:bg-[var(--error-subtle)]"
                >
                  {m.common_delete()}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Company Panel Drawer */}
      {job.url && (() => {
        try {
          const domain = new URL(job.url).hostname.replace(/^www\./, "");
          return (
            <CompanyPanel
              open={panelOpen}
              onClose={() => setPanelOpen(false)}
              domain={domain}
              salaryData={salaryData && salaryData.q25 ? {
                q25: salaryData.q25,
                median: salaryData.median!,
                q75: salaryData.q75!,
                source: salaryData.source ?? "Salary data",
              } : null}
            />
          );
        } catch {
          return null;
        }
      })()}
    </div>
  );
}

function RequirementsDisplay({ requirements }: { requirements: ParsedRequirements }) {
  return (
    <div className="space-y-4">
      {requirements.requiredSkills.length > 0 && (
        <div>
          <p className="text-xs font-medium text-[var(--text-secondary)] mb-2">{m.jobs_required_skills()}</p>
          <div className="flex flex-wrap gap-1.5">
            {requirements.requiredSkills.map((s) => (
              <span key={s} className="badge badge-accent">{s}</span>
            ))}
          </div>
        </div>
      )}
      {requirements.niceToHaveSkills?.length ? (
        <div>
          <p className="text-xs font-medium text-[var(--text-secondary)] mb-2">{m.jobs_nice_to_have()}</p>
          <div className="flex flex-wrap gap-1.5">
            {requirements.niceToHaveSkills.map((s) => (
              <span key={s} className="badge badge-default">{s}</span>
            ))}
          </div>
        </div>
      ) : null}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {requirements.seniority && (
          <div className="rounded-lg bg-[var(--bg-secondary)] p-3">
            <p className="text-[10px] font-medium uppercase tracking-wider text-[var(--text-muted)]">{m.jobs_seniority()}</p>
            <p className="mt-0.5 text-sm font-medium text-[var(--text)]">{requirements.seniority}</p>
          </div>
        )}
        {requirements.industry && (
          <div className="rounded-lg bg-[var(--bg-secondary)] p-3">
            <p className="text-[10px] font-medium uppercase tracking-wider text-[var(--text-muted)]">{m.jobs_industry()}</p>
            <p className="mt-0.5 text-sm font-medium text-[var(--text)]">{requirements.industry}</p>
          </div>
        )}
      </div>
      {requirements.languageRequirements?.length ? (
        <div>
          <p className="text-xs font-medium text-[var(--text-secondary)] mb-2">{m.jobs_languages_required()}</p>
          <div className="flex flex-wrap gap-1.5">
            {requirements.languageRequirements.map((l) => (
              <span key={l.language} className="badge badge-default">
                {l.language}{l.level ? ` (${l.level})` : ""}
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
